import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useLifecycleStage } from '@/hooks/use-asset-management-widgets';
import { TrendingUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';

// Use the same colors as Warranty Status widget
const STAGE_COLORS: Record<string, string> = {
  'new': '#10b981', // green-500
  'active': '#3b82f6', // blue-500
  'mature': '#f59e0b', // amber-500
  'aging': '#f97316', // orange-500
  'end of life': '#ef4444', // red-500
  'endoflife': '#ef4444', // red-500
  'pending': '#a855f7', // purple-500
  'inactive': '#6b7280', // gray-500
  'disposed': '#78716c', // stone-500
  'retired': '#6b7280', // gray-500
};

const STAGE_ORDER = ['New', 'Active', 'Mature', 'Aging', 'End of Life'];

// Helper function to convert status to title case
const toTitleCase = (str: string): string => {
  return str
    .split(/[\s_-]+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export function LifecycleStageWidget() {
  const { data, isLoading, error } = useLifecycleStage();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <Card className="relative overflow-hidden shadow-sm bg-white dark:bg-black h-full flex flex-col">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-1.5">
              <CardTitle className="text-base font-semibold tracking-tight">Asset Lifecycle Stage</CardTitle>
              <CardDescription>Distribution by lifecycle stage</CardDescription>
            </div>
            <div className="p-2 bg-indigo-50 dark:bg-indigo-950 rounded-lg">
              <TrendingUp className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex-1">
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  // Handle error state (API failure)
  if (error) {
    return (
      <Card className="relative overflow-hidden shadow-sm bg-white dark:bg-black h-full flex flex-col">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-1.5">
              <CardTitle className="text-base font-semibold tracking-tight">Asset Lifecycle Stage</CardTitle>
              <CardDescription>Distribution by lifecycle stage</CardDescription>
            </div>
            <div className="p-2 bg-indigo-50 dark:bg-indigo-950 rounded-lg">
              <TrendingUp className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex-1">
          <div className="flex h-[300px] items-center justify-center text-muted-foreground">
            Unable to load lifecycle data
          </div>
        </CardContent>
      </Card>
    );
  }

  // Handle empty data state (no assets in database)
  if (!data?.stages?.length) {
    return (
      <Card className="relative overflow-hidden shadow-sm bg-white dark:bg-black h-full flex flex-col">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-1.5">
              <CardTitle className="text-base font-semibold tracking-tight">Asset Lifecycle Stage</CardTitle>
              <CardDescription>Distribution by lifecycle stage</CardDescription>
            </div>
            <div className="p-2 bg-indigo-50 dark:bg-indigo-950 rounded-lg">
              <TrendingUp className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex-1">
          <div className="flex flex-col h-[300px] items-center justify-center text-center">
            <TrendingUp className="h-12 w-12 text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">
              No lifecycle data available
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Sort data by stage order
  const chartData = [...data.stages].sort((a, b) => {
    return STAGE_ORDER.indexOf(a.stage) - STAGE_ORDER.indexOf(b.stage);
  });

  // Calculate max value for scaling
  const maxValue = Math.max(...chartData.map(d => d.count));
  const totalAssets = chartData.reduce((sum, d) => sum + d.count, 0);

  // Handle bar click to navigate to filtered assets
  const handleStageClick = (stage: string) => {
    navigate(`/assets?lifecycle=${encodeURIComponent(stage)}`);
  };

  return (
    <Card className={cn('relative overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 bg-white dark:bg-black h-full flex flex-col')}>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="space-y-1.5">
            <CardTitle className="text-base font-semibold tracking-tight">Asset Lifecycle Stage</CardTitle>
            <CardDescription>Distribution by lifecycle stage</CardDescription>
          </div>
          <div className="p-2 bg-indigo-50 dark:bg-indigo-950 rounded-lg">
            <TrendingUp className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex-1">
        <div className="space-y-6">
          {/* Total Assets Display */}
          <div className="flex items-baseline gap-2">
            <div className="text-3xl font-bold">
              {totalAssets}
            </div>
            <div className="text-sm text-muted-foreground">
              Total Assets
            </div>
          </div>

          {/* Bar Chart */}
          <div className="space-y-4">
            {chartData.map((item) => {
              const percentage = maxValue > 0 ? (item.count / maxValue) * 100 : 0;
              
              return (
                <button 
                  key={`stage-${item.stage}`}
                  type="button"
                  className="w-full space-y-2 cursor-pointer group text-left"
                  onClick={() => handleStageClick(item.stage)}
                  aria-label={`View assets in ${item.stage} stage`}
                >
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div 
                        className="h-3 w-3 rounded-full transition-transform group-hover:scale-110" 
                        style={{ backgroundColor: item.color ?? STAGE_COLORS[item.stage.toLowerCase()] ?? '#6b7280' }}
                      />
                      <span className="font-medium group-hover:text-foreground transition-colors">{toTitleCase(item.stage)}</span>
                    </div>
                    <span className="font-semibold group-hover:text-foreground transition-colors">
                      {item.count} assets
                    </span>
                  </div>
                  <div className="relative h-8 w-full overflow-hidden rounded-md bg-muted group-hover:bg-muted/80 transition-colors">
                    <div
                      className="h-full transition-all duration-500 ease-out group-hover:brightness-110"
                      style={{
                        width: `${percentage}%`,
                        backgroundColor: item.color ?? STAGE_COLORS[item.stage.toLowerCase()] ?? '#6b7280',
                      }}
                    />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
